import { useEffect, useMemo, useState } from "react";
import { useResource } from "@/hooks/useResource";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, AlertTriangle, Package } from "lucide-react";

const num = (v) => (v === "" || v == null ? 0 : Number(v));

export function SalesBillBuilder({ open, onOpenChange, onCreated }) {
  const { data: buyerData } = useResource("/buyers");
  const { data: prodData } = useResource("/products");
  const { data: fgData } = useResource("/finished-goods");

  const buyers = useMemo(() => (Array.isArray(buyerData) ? buyerData : []), [buyerData]);
  const products = useMemo(() => (Array.isArray(prodData) ? prodData : []).filter((p) => p.active), [prodData]);
  const fgBySku = useMemo(() => {
    const m = {};
    (Array.isArray(fgData) ? fgData : []).forEach((f) => {
      m[f.sku] = Math.max(0, (Number(f.onHand) || 0) - (Number(f.reserved) || 0));
    });
    return m;
  }, [fgData]);

  const [buyerId, setBuyerId] = useState("");
  const [billDate, setBillDate] = useState(new Date().toISOString().slice(0, 10));
  const [lines, setLines] = useState([]);
  const [addProd, setAddProd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setBuyerId(""); setLines([]); setAddProd("");
      setBillDate(new Date().toISOString().slice(0, 10));
    }
  }, [open]);

  const resolvePrice = async (productId) => {
    if (!buyerId) return { rate: 0, source: "NONE", gst: 0 };
    try {
      const { data } = await api.get(`/buyer-prices/resolve`, { params: { buyerId, productId } });
      if (data && data.sellingPrice != null) {
        return { rate: Number(data.sellingPrice), source: "BUYER_PRICE",
                 gst: (Number(data.cgstPct) || 0) + (Number(data.sgstPct) || 0) + (Number(data.igstPct) || 0) };
      }
    } catch (e) { /* ignore */ }
    return { rate: 0, source: "NONE", gst: 0 };
  };

  const addLine = async () => {
    if (!addProd) return;
    if (lines.some((l) => l.productId === addProd)) { toast.info("Product already added"); return; }
    const p = products.find((x) => x.productId === addProd);
    const price = await resolvePrice(addProd);
    setLines((prev) => [...prev, {
      productId: p.productId, sku: p.sku, name: p.name,
      unit: p.productUnit || p.unit || "pcs", qty: 1,
      rate: price.rate, priceSource: price.source, gstPct: price.gst, discountPct: 0,
    }]);
    setAddProd("");
  };

  const upd = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const totals = useMemo(() => {
    let sub = 0, disc = 0, tax = 0, grand = 0;
    lines.forEach((l) => {
      const gross = num(l.qty) * num(l.rate);
      const d = (gross * num(l.discountPct)) / 100;
      const taxable = gross - d;
      const g = (taxable * num(l.gstPct)) / 100;
      sub += gross; disc += d; tax += g; grand += taxable + g;
    });
    return { sub, disc, tax, grand };
  }, [lines]);

  const save = async () => {
    if (!buyerId) { toast.error("Select a buyer"); return; }
    if (lines.length === 0) { toast.error("Add at least one product"); return; }
    setSaving(true);
    try {
      const res = await api.post("/sales-bills", {
        buyerId, billDate,
        lines: lines.map((l) => ({
          productId: l.productId, qty: num(l.qty),
          rate: l.priceSource === "NONE" && num(l.rate) === 0 ? undefined : num(l.rate),
          discountPct: num(l.discountPct), gstPct: num(l.gstPct), unit: l.unit,
        })),
      });
      toast.success(`Sales bill ${res.data.billNo} saved as draft`);
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not create sales bill"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-white" data-testid="sales-bill-builder">
        <DialogHeader>
          <DialogTitle className="font-display">New Sales Bill</DialogTitle>
          <DialogDescription>Buyer-specific pricing is applied automatically. Draft does not reserve stock.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Buyer</Label>
            <Select value={buyerId} onValueChange={(v) => { setBuyerId(v); setLines([]); }}>
              <SelectTrigger data-testid="sb-buyer-select" className="h-9"><SelectValue placeholder="Select buyer" /></SelectTrigger>
              <SelectContent>
                {buyers.map((b) => <SelectItem key={b.buyerId} value={b.buyerId} data-testid={`sb-buyer-${b.buyerId}`}>{b.name} · {b.buyerId}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Bill Date</Label>
            <Input data-testid="sb-bill-date" type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)} className="h-9" />
          </div>
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">Add Product (active only · shows available FG)</Label>
            <Select value={addProd} onValueChange={setAddProd} disabled={!buyerId}>
              <SelectTrigger data-testid="sb-product-select" className="h-9"><SelectValue placeholder={buyerId ? "Select product" : "Select buyer first"} /></SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={p.productId} value={p.productId} data-testid={`sb-product-${p.productId}`}>
                    {p.name} · {p.productId} · FG {fgBySku[p.sku] ?? 0}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="sb-add-line" type="button" variant="outline" className="h-9 gap-1.5" onClick={addLine} disabled={!addProd}>
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>

        <div className="max-h-72 overflow-auto rounded-lg border border-stone-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-stone-50 text-left text-[10px] uppercase text-stone-500">
              <tr>
                <th className="px-3 py-2">Product</th><th className="px-2 py-2 text-right">Avail FG</th>
                <th className="px-2 py-2 w-20 text-right">Qty</th><th className="px-2 py-2 w-24 text-right">Rate</th>
                <th className="px-2 py-2 w-16 text-right">Disc%</th><th className="px-2 py-2 w-16 text-right">GST%</th>
                <th className="px-2 py-2 text-right">Amount</th><th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {lines.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No products added yet.</td></tr>
              ) : lines.map((l, i) => {
                const avail = fgBySku[l.sku] ?? 0;
                const gross = num(l.qty) * num(l.rate);
                const amount = gross - (gross * num(l.discountPct)) / 100;
                const shortFG = num(l.qty) > avail;
                return (
                  <tr key={l.productId} data-testid={`sb-line-${i}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-stone-800">{l.name}</div>
                      <div className="flex items-center gap-1 text-[10px] text-stone-400">
                        {l.productId}
                        {l.priceSource === "NONE" && (
                          <span data-testid={`sb-line-nobuyerprice-${i}`} className="inline-flex items-center gap-0.5 text-amber-700">
                            <AlertTriangle className="h-3 w-3" /> No buyer-specific price
                          </span>
                        )}
                        {l.priceSource === "BUYER_PRICE" && <span className="text-emerald-600">buyer price</span>}
                      </div>
                    </td>
                    <td className={`px-2 py-2 text-right font-mono ${shortFG ? "text-rose-600" : "text-stone-500"}`}>{avail}</td>
                    <td className="px-2 py-2"><Input data-testid={`sb-qty-${i}`} type="number" min="0" value={l.qty} onChange={(e) => upd(i, { qty: e.target.value })} className="h-8 text-right" /></td>
                    <td className="px-2 py-2"><Input data-testid={`sb-rate-${i}`} type="number" min="0" value={l.rate} onChange={(e) => upd(i, { rate: e.target.value })} className="h-8 text-right" /></td>
                    <td className="px-2 py-2"><Input type="number" min="0" value={l.discountPct} onChange={(e) => upd(i, { discountPct: e.target.value })} className="h-8 text-right" /></td>
                    <td className="px-2 py-2"><Input type="number" min="0" value={l.gstPct} onChange={(e) => upd(i, { gstPct: e.target.value })} className="h-8 text-right" /></td>
                    <td className="px-2 py-2 text-right font-mono">{fmtCurrency(amount)}</td>
                    <td className="px-2 py-2"><button data-testid={`sb-remove-${i}`} onClick={() => removeLine(i)} className="text-stone-300 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end gap-6 rounded-lg bg-stone-50 px-4 py-2 text-sm">
          <span className="text-stone-500">Subtotal <b className="font-mono text-stone-800">{fmtCurrency(totals.sub)}</b></span>
          <span className="text-stone-500">Disc <b className="font-mono text-stone-800">{fmtCurrency(totals.disc)}</b></span>
          <span className="text-stone-500">Tax <b className="font-mono text-stone-800">{fmtCurrency(totals.tax)}</b></span>
          <span className="text-stone-800">Grand Total <b data-testid="sb-grand-total" className="font-mono text-base text-amber-700">{fmtCurrency(totals.grand)}</b></span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="sb-save-draft" className="gap-1.5 bg-amber-700 hover:bg-amber-800" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />} Save Draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
